import _ from 'lodash';
import {
  AD_TYPE,
  NORMAL_AD_SOURCES,
  NORMAL_AD_SOURCES_REVERSE_MAP,
  DPA_WORD_PATTERN_LEFT,
  DPA_WORD_PATTERN_RIGHT,
  CAROUSEL_IMAGE_MODE,
  CARD_ADDITIONAL_CREATIVE_TYPE,
  identityTypeMap,
  identityAvailableStatus,
  CREATIVE_TYPE,
  NATIVE_AD_ACTION_TYPE,
  imageMode,
  nativeAdIdentityTypes,
  bcIdentityTypesList,
  DARK_POST_STATUS_MAP,
} from 'src/const/state/creative';

import { DPA_VIDEO_DISPLAY_MODE_TYPE } from 'src/const/state/ad';
import { Scenario } from 'src/const/state/shop';
import uuidv4 from 'uuid/v4';
import { addIdentity, getIdentityInfo, getIdentityStatus } from 'src/service/index';
import { isCreativeHasIdentityWhite } from 'src/const/whiteList';
import { canEdit, getEditAidOrCid } from 'src/utils/authUtil';
import State, { TEMPLATE } from './state';

const { BC_AUTH_TT } = identityTypeMap;

const {
  SINGLE_IMAGE, SINGLE_VIDEO, CATALOGUE_VIDEO, CATALOGUE_IMAGE, CAROUSEL_IMAGE, REAL_TIME_LIVE,
} = AD_TYPE;
const { CREATE_NEW, NATIVE_VIDEO } = NORMAL_AD_SOURCES;
const {
  Available,
  NO_DATA, NOT_IDENTITY, IS_PRIVATE_POST,
  USER_NO_PRIVILEGE, BC_UN_BIND_ACCOUNT, BC_NO_VIDEO_RIGHT, BC_NO_VALID_BIND_ACCOUNT, BC_NO_LIVE_RIGHT,
} = identityAvailableStatus;

// 提交给后端的标题中，要求的动态词包格式和前端展示的不同，提交前需要转换一次，是transformTitle4Frontend的逆过程
// [品类]普通文案 => {$品类$}普通文案
export const transformTitle4Backend = (title = '', dpaWordList = [], selectedWordId = []) => {
  let transformedTitle = title.trim();
  // 根据词包id找到词包配置
  const selectedWordList = selectedWordId.reduce((result, wordId) => {
    const word = dpaWordList.find(item => item.lexiconID === wordId);
    return word ? result.concat(word) : result;
  }, []);
  // 针对每个用到的词包进行替换
  selectedWordList.forEach((word) => {
    transformedTitle = transformedTitle.replace(
      `${ DPA_WORD_PATTERN_LEFT }${ word.name }${ DPA_WORD_PATTERN_RIGHT }`,
      `{$${ word.name }$}`,
    );
  });
  return transformedTitle;
};

export const isValidAdSource = adSource => !!NORMAL_AD_SOURCES_REVERSE_MAP[adSource];

// 模板
export const getAdTemplateByType = (adSource, adType) => {
  const result = _.cloneDeep(TEMPLATE);
  result.adSource = adSource;
  if (result.adSource) {
    result[adSource].adType = adType;
  }
  return result;
};

// 根据素材信息推算adType
export const getAdTypeByDetail = (adDetail) => {
  const keyCount = obj => Object.keys(obj).length;

  // 原生广告也有可能出现 video_info为空的情况 所以这里要 前置判断
  // 原生广告 通过native_source 来判断adType
  if (adDetail.native_source === NATIVE_VIDEO.value || nativeAdIdentityTypes.includes(adDetail.identity_type)) {
    return adDetail.creative_type === CREATIVE_TYPE.SHOP_LIVE_REAL_TIME ? REAL_TIME_LIVE.value : SINGLE_VIDEO.value;
  }
  if (!adDetail || (!adDetail.video_info && !adDetail.image_info)) {
    return SINGLE_VIDEO.value;
  }
  const carouselImageModeList = Object.values(CAROUSEL_IMAGE_MODE);
  const isCarouselImageCreative = carouselImageModeList.includes(+adDetail.image_mode);
  if (isCarouselImageCreative) {
    return CAROUSEL_IMAGE.value;
  }

  const isDpaImgMode = adDetail.image_mode === imageMode.IMAGE_I18N_SQUARE;
  if (adDetail.taskId) return SINGLE_VIDEO.value;
  if (adDetail.video_info) {
    // 目录视频创意的video_info是空对象
    // 目录图片创意的image_info是空对象
    if (keyCount(adDetail.video_info)) {
      return SINGLE_VIDEO.value;
    }
    if (isDpaImgMode) {
      return CATALOGUE_IMAGE.value;
    }
    return CATALOGUE_VIDEO.value;
  }
  if (adDetail.image_info && adDetail.image_info.length === 1) {
    return SINGLE_IMAGE.value;
  }
  return '';
};

// 根据素材信息推算adSource
export const getAdSourceByDetail = (adDetail) => {
  const { native_source, identity_type } = adDetail || {};
  const isNative = (native_source === NATIVE_VIDEO.value) || nativeAdIdentityTypes.includes(identity_type);
  return isNative ? NATIVE_VIDEO.value : CREATE_NEW.value;
};
const exchangeCarouselImageAdDetail = (adDetail) => {
  const {
    image_mode, titles = [], image_material_ids = [], external_urls = [], external_url_material_ids = [],
  } = adDetail;
  adDetail.image_info = adDetail.image_info.map((image, index) => {
    const { web_uri } = image;
    const origin = web_uri;
    const title = titles[index] || image.title || '';
    const externalUrl = external_urls[index] || image.externalUrl || '';
    const uuid = uuidv4();
    const materialIds = [image_material_ids[index], external_url_material_ids[index]];
    const restProp = {
      origin, title, [image_mode]: origin, uuid, materialIds, externalUrl,
    };
    return Object.assign({}, image, restProp);
  });
};
// 将adDetail转为ad
export const transformDetail2Ad = (adDetail) => {
  const adSource = getAdSourceByDetail(adDetail);
  const adType = getAdTypeByDetail(adDetail);
  const { CAROUSEL_IMAGE } = AD_TYPE;
  if (adType === CAROUSEL_IMAGE.value) {
    exchangeCarouselImageAdDetail(adDetail);
  }
  const { format_structure = CARD_ADDITIONAL_CREATIVE_TYPE.CUSTOMIZED.value } = adDetail;
  const ad = _.cloneDeep(TEMPLATE);
  ad.adSource = adSource;
  if (adType && ad[adSource][adType]) {
    // 已有具体素材时，回填对应的adDetail中
    ad[adSource][adType] = {
      ...ad[adSource][adType],
      ..._.cloneDeep(adDetail),
      format_structure,
    };
    ad[adSource].adType = adType;
  }
  return ad;
};

// 判断一个ad是否还没有设置关键素材，即是“空的”
export const isAdEmpty = (ad = {}) => {
  const { adSource } = ad;
  if (!isValidAdSource(adSource)) {
    return true;
  }

  const isSourceTargetEmpty = (sourceTarget) => {
    const { adType } = sourceTarget;
    if (!adType) {
      return true;
    }
    // 如果当前是直投直播间，则直接判断为非空
    if (adType === REAL_TIME_LIVE.value) {
      return false;
    }

    return Object.keys(sourceTarget)
      .filter(key => key !== 'adType')
      .every((key) => { // 使用every确保每种类型下都是空的
        const adType = +key; // to int
        const adDetail = sourceTarget[adType] || {};

        // 单图\多图轮播、单视频判断图片、视频是否空的
        if (adType === SINGLE_IMAGE.value
            || adType === SINGLE_VIDEO.value
            || adType === CAROUSEL_IMAGE.value) {
          return !adDetail.image_info && !adDetail.video_info;
        }
        // 商品库图片、商品库视频，由于没有图片、视频，转为判断其他素材是否都是空的
        if (adType === CATALOGUE_IMAGE.value || adType === CATALOGUE_VIDEO.value) {
          return !adDetail.title; // creative_name在勾选自动生成时非必填，call_to_action在品牌竞价中非必填
        }
        // 由于当前不是直投直播间，直投直播间创意没有video_info， 所以应该直接判断为空，避免影响其他adtype的判断
        if (adType === REAL_TIME_LIVE.value) {
          return true;
        }
        return false;
      });
  };

  return Object.keys(ad)
    .filter(key => key !== 'adSource')
    .every((key) => { // 使用every确保每种source下都是空的
      const sourceTarget = ad[key];
      return isSourceTargetEmpty(sourceTarget);
    });
};

/**
 * 对存量数据的处理：
 * adDetail 没有identity_id 说明是历史数据 将identityAuthorizationState设为-3 表示没有拿到identity数据 这是为了在页面顶部显示alert warning
 * 对于普通ad的处理：为用户自动创建一个identity假账号 并回填创建完成之后的相关信息 用户不用任何操作
 * 对于原生ad的处理：默认不回显填充identity，不自动创建账号，需要用户手动选择
 * 对于程序化创意的处理：为用户自动创建一个identity假账号 并回填创建完成之后的相关信息 用户不用任何操作
 */
export const setHistoryIdentityData = async (adDetail) => {
  // 没有app_name、source 说明为下沉前数据 删除identity相关字段
  const isCreativeAccount = adDetail.app_name || adDetail.source;
  if (!isCreativeAccount || !canEdit()) {
    delete adDetail.identity_id;
    delete adDetail.identity_type;
    delete adDetail.identity_data;
    return { adDetail };
  }

  const availableStatus = NOT_IDENTITY;
  const isNativeAd = adDetail.native_source && (adDetail.native_source === NATIVE_VIDEO.value);
  if (isNativeAd) {
    adDetail.identity_data = {
      identity_type: identityTypeMap.AUTH_CODE_USER,
    };
    return {
      adDetail,
      availableStatus,
    };
  }
  const params = {
    identity_type: identityTypeMap.FAKE_USER,
    profile_image: adDetail.avatar_icon?.web_uri || '',
    display_name: adDetail.app_name || adDetail.source,
  };
  const res = await addIdentity(params);
  if (res && res.code === 0) {
    const { identity_id = '', identity_type = 0 } = res.data?.identity_info;
    adDetail.identity_id = identity_id;
    adDetail.identity_type = identity_type;
    adDetail.identity_data = res.data.identity_info;
    // Vue.prototype.$tea.trackEvent('add_fake_identity_success');
  } else {
    adDetail.identity_id = '';
    adDetail.identity_type = identityTypeMap.FAKE_USER;
    adDetail.identity_data = {
      identity_type: identityTypeMap.FAKE_USER,
    };
  }
  return {
    adDetail,
    availableStatus,
  };
};

// 复制ad group时对创意的identity数据做权限处理
export const setIdentityDataList = async (list, isShop) => {
  const newList = _.cloneDeep(list);
  for (let i = 0, len = newList.length; i < len; i++) {
    // eslint-disable-next-line
    const { adDetail = {} } = await setIdentityData(newList[i], false, isShop);
    newList[i] = adDetail;
  }
  return newList;
};

async function setShopAdsIdentityData(data, isEdit = false) {
  let availableStatus = 0;
  const {
    identity_id, identity_type, identity_bc_id, external_type,
  } = data;
  const params = {
    identity_id,
    identity_type,
  };
  // 只有bc授权账号才需要传identity_bc_id
  if (bcIdentityTypesList.includes(identity_type)) {
    params.identity_bc_id = identity_bc_id || '';
  }
  const resp = await getIdentityInfo(params);
  // 为空有三种场景：已解绑 被封号 当前user没有该identity权限
  if (_.isEmpty(resp)) {
    // 若是bc账号，则一定是解绑状态(不需要判断是否是当前user)
    if (bcIdentityTypesList.includes(identity_type)) {
      return {
        available_status: BC_UN_BIND_ACCOUNT,
      };
    }
    const status = await getIdentityStatus(params);
    // 若获取不到identityInfo，则获取identity的status。若等于-1，代表已解绑或被封号；否则代表当前user没有该identity权限
    // 若status是已解绑 被封号，则直接返回状态。否则，如果是编辑场景，就用附身参数请求identity数据进行返回
    if (status === NO_DATA) {
      return {
        available_status: NO_DATA,
      };
    }
    // 证明是当前user没有该identity权限，若不是编辑场景，则返回USER_NO_PRIVILEGE
    if (!isEdit) {
      return {
        available_status: USER_NO_PRIVILEGE,
      };
    }
    // 若是编辑场景，params中追加attach_aid / attach_cid，以保证能正常回显
    const attachId = getEditAidOrCid() || {}; // 先去除
    const respByAttachUser = await getIdentityInfo({
      ...params,
      ...attachId,
    });
    return {
      ...respByAttachUser,
      available_status: USER_NO_PRIVILEGE,
    };
  }
  const { available_status = 0, can_use_video_list = true, can_use_live_list = true } = resp || {};
  const isAvailableStatus = available_status === Available;

  /**
     * BC权限处理
     * 1. availableStatus = 0，需要判断是否有获取视频权限，若没有，则需要视为不可用，availableStatus为前端自定义标识 BC_NO_VIDEO_RIGHT
     * 2. availableStatus = -4，表示该账号因为其他原因导致为不可使用，为了区分于TTBA账号的状态，availableStatus为前端自定义标识 BC_NO_VALID_BIND_ACCOUNT
     */
  // 为bc授权账号 且没有获取视频权限
  const isBCIdentity = data.identity_type === BC_AUTH_TT;
  const bcIdentityPreCond = isBCIdentity && isAvailableStatus;
  const isLiveShopping = external_type && external_type === Scenario.Live || State.isShopAdLiveScenario;
  const isVideoShopping = !isLiveShopping;
  const canBcIdentityUseVideo = bcIdentityPreCond && can_use_video_list;
  const canBcIdentityUseLive = bcIdentityPreCond && can_use_live_list;
  if (isBCIdentity) {
    switch (available_status) {
      case Available:
        if (isVideoShopping && !canBcIdentityUseVideo) {
          availableStatus = BC_NO_VIDEO_RIGHT;
        } else if (isLiveShopping && !canBcIdentityUseLive) {
          availableStatus = BC_NO_LIVE_RIGHT;
        } else {
          availableStatus = Available;
        }
        break;
      case USER_NO_PRIVILEGE:
        availableStatus = BC_NO_VALID_BIND_ACCOUNT;
        break;
      default:
        availableStatus = available_status;
        break;
    }
  } else {
    availableStatus = available_status;
  }

  resp.available_status = availableStatus;

  return resp;
}

// 判断creative的identity账号权限
async function getIdentityPermissionInfo(data, isEdit, isShop) {
  const adDetail = _.cloneDeep(data);
  let availableStatus = 0;
  const { creative_type } = adDetail;
  const params = {
    identity_id: adDetail.identity_id,
    identity_type: adDetail.identity_type,
  };

  // 只有bc授权账号才需要传identity_bc_id
  if (bcIdentityTypesList.includes(adDetail.identity_type)) {
    params.identity_bc_id = adDetail.identity_bc_id || '';
  }

  const resp = await getIdentityInfo(params);
  // 为空有三种场景：已解绑 被封号 当前user没有该identity权限
  if (_.isEmpty(resp)) {
    const status = bcIdentityTypesList.includes(adDetail.identity_type) ? BC_UN_BIND_ACCOUNT : NO_DATA;
    adDetail.identity_data.identity_id = '';
    availableStatus = status;
  } else {
    const { available_status = 0, can_use_video_list = true, can_use_live_list = true } = resp || {};
    adDetail.identity_data = resp;
    const isAvailableStatus = available_status === Available;

    /**
     * BC权限处理
     * 1. availableStatus = 0，需要判断是否有获取视频权限，若没有，则需要视为不可用，availableStatus为前端自定义标识 BC_NO_VIDEO_RIGHT
     * 2. availableStatus = -4，表示该账号因为其他原因导致为不可使用，为了区分于TTBA账号的状态，availableStatus为前端自定义标识 BC_NO_VALID_BIND_ACCOUNT
     */
    // 为bc授权账号 且没有获取视频权限
    const isBCIdentity = adDetail.identity_type === BC_AUTH_TT;
    const isNoVideoPowerForBcIdentity = isBCIdentity && isAvailableStatus && !can_use_video_list;
    const bcIdentityPreCond = isBCIdentity && isAvailableStatus;
    const canBcIdentityUseLive = bcIdentityPreCond && can_use_live_list;
    if (isBCIdentity) {
      switch (available_status) {
        case Available:
          availableStatus = isNoVideoPowerForBcIdentity ? BC_NO_VIDEO_RIGHT : available_status;
          /*
            identity bc使用real time live需要live权限,
            使用 live video 需要live和video权限，因为需要拉取post视频
          */
          if (State.isShopAdLiveScenario && !canBcIdentityUseLive) { // live场景没有live权限
            availableStatus = BC_NO_LIVE_RIGHT;
          } else if (State.isShopAdLiveScenario && creative_type === CREATIVE_TYPE.SHOP_LIVE_VIDEO && !can_use_video_list) {
            availableStatus = BC_NO_LIVE_RIGHT; // standard video没有video权限
          } else if (State.isShopAdLiveScenario) {
            /*
               能进入这个判断说明
               1.有live权限，有video权限（权限都具备，不关注是那种creative type）
               2.real live 有live权限
            */
            availableStatus = Available;
          }
          break;
        case USER_NO_PRIVILEGE:
          availableStatus = BC_NO_VALID_BIND_ACCOUNT;
          break;
        default:
          availableStatus = available_status;
          break;
      }
    } else {
      availableStatus = available_status;
    }
    // 当前identity账号可用 post不可用 可能视频设为隐私/下架 （直投直播间没有video_info，排除）
    const isNoVideoInfo = isAvailableStatus && (!adDetail.video_info || _.isEmpty(adDetail.video_info));
    const isRealTimeLive = adDetail.creative_type === CREATIVE_TYPE.SHOP_LIVE_REAL_TIME;
    const isBCAvailableStatus = !isBCIdentity || (isBCIdentity && can_use_video_list);
    if (isNoVideoInfo && !isRealTimeLive && isBCAvailableStatus) {
      availableStatus = IS_PRIVATE_POST;
    } else if (!isShop && (!isAvailableStatus || isNoVideoPowerForBcIdentity)) {
      // shop的identity展示依赖adDetail.identity_data,清空的话无法回显示
      // 若账号不是可用状态 需要提示原因 并引导重新选择
      // adDetail.identity_id = '';
      adDetail.identity_data.identity_id = '';
      if (!isEdit) {
        adDetail.identity_id = '';
        adDetail.identity_data = {
          identity_type: adDetail.identity_type,
        };
      }
    }
  }

  return {
    adDetail,
    availableStatus,
  };
}

// 对创意的identity数据做权限处理
export const setIdentityData = async (data, isEdit, isShop = false, isCallByShopAds = false) => {
  if (isShop && isCallByShopAds) {
    return setShopAdsIdentityData(data, isEdit);
  }
  let adDetail = _.cloneDeep(data);
  let availableStatus = 0;
  /**
   * 对于增量数据的处理：
   * 处理账号权限 这里只处理两种identity类型
   * 1. TTBA类型的identity（identity_type = 3）:编辑创意时首先判断identity账号的授权状态 如果授权发生变化 在页面显示相应的横幅提示
   * 2. bc授权绑定的identity（identity_type = 5）:
   */
  if (adDetail.identity_id) {
    const isTTBAIdentityNativeAd = adDetail.identity_type && adDetail.identity_type === identityTypeMap.TT_USER;
    const isBCAuthIdentityNativeAd = adDetail.identity_type && adDetail.identity_type === identityTypeMap.BC_AUTH_TT;

    const isShowCheckPermission = isTTBAIdentityNativeAd || isBCAuthIdentityNativeAd;

    if (isShowCheckPermission) {
      const { adDetail: newAdDetail, availableStatus: status } = await getIdentityPermissionInfo(adDetail, isEdit, isShop);
      adDetail = newAdDetail || {};
      availableStatus = status;
    }
    return {
      adDetail,
      availableStatus,
    };
  }
  // 处理存量数据
  const historyDetail = await setHistoryIdentityData(adDetail);
  return historyDetail;
};


// eslint-disable-next-line max-lines-per-function
export const fillCreativeInfo = (item) => {
  const isNativeAd = Object.values(NATIVE_AD_ACTION_TYPE).includes(item.item_source);
  const [native_source, nativeAdActionType] = isNativeAd
    ? [NATIVE_VIDEO.value, item.item_source]
    : [CREATE_NEW.value, undefined];
  const newItem = {
    // 看下这里能把item整个深拷贝给newitem吗，这里创意复制每次新增字段都得来改一遍，艾特帅东@黄晓东
    image_material_ids: item.image_material_ids || [],
    external_url_material_ids: item.external_url_material_ids || [],
    external_urls: item.external_urls || [],
    video_tpl_name: item.video_tpl_name || '',
    video_display_mode: item.video_display_mode || DPA_VIDEO_DISPLAY_MODE_TYPE.AUTO,
    titles: item.titles,
    call_to_action: item.call_to_action,
    title: item.title,
    app_name: item.app_name,
    source: item.source || '',
    dpa_open_url_type: item.dpa_open_url_type || 0,
    params_type: item.params_type || 0,
    fallback_type: item.fallback_type || 0,
    open_url: item.open_url,
    external_url: item.external_url || '',
    struct_version: item.struct_version,
    is_open_url: item.is_open_url,
    auto_open: item.auto_open,
    avatar_icon: item.avatar_icon,
    action_track_url: item.action_track_url,
    track_url: item.track_url,
    creative_name: item.creative_name,
    image_info: item.image_info || [],
    image_mode: item.image_mode,
    autoCreativeName: !item.creative_name,
    dpa_word_set: item.dpa_word_set || [],
    native_source,
    nativeAdActionType,
    aweme_item_id: item.aweme_item_id, // 后端接口需要，前端展示不用
    vast_moat: item.vast_moat || 0,
    vast_double_verify: item.vast_double_verify || 0,
    vast_ias: item.vast_ias || false,
    vast_url: item.vast_url || '',
    is_creative_authorized: !!item.is_creative_authorized, // 创意素材授权
    advanced_creative_type: item.advanced_creative_type,
    call_to_action_id: item.call_to_action_id, // CTA 优选集合ID
    creative_type: item.creative_type, // 创意类型，用于判断是否是 PLP/PDP 创意
    format_structure: item.format_structure, // 附加创意类型 0-自定义 1-collection ads
    card_id: item.card_id || item.gesture_id || item.painted_egg_id, // 附加创意卡片id
    page_id: item.page_id, // 附加创意page id
    brand_safety_postbid_partner: item.brand_safety_postbid_partner,
    brand_safety_vast_url: item.brand_safety_vast_url,
    domain: item.domain,

    disclaimer_type: item.disclaimer_type,
    disclaimer_text: item.disclaimer_text,
    disclaimer_clickable_texts: item.disclaimer_clickable_texts,
    rf_creative_id: item.creative_id ?? '',
    spu_id: item.spu_id,
    spu_list: item.spu_list || [],
    product_set_id: item.product_set_id,
    product_selection_type: item.product_selection_type,
    // 新增暗投模式复制；
    dark_post_status: item.dark_post_status,
    bc_restrict_delivery: item.bc_restrict_delivery,
    is_use_product_anchor: item.is_use_product_anchor || false, // shop是否使用锚点投放
  };

  if (item.item_source === NATIVE_VIDEO.value) {
    newItem.with_promotional_music = item.with_promotional_music !== false; // 是否打开推广音乐开关
    newItem.item_duet_disabled = item.item_duet_disabled || 0; // 是否开启合拍
    newItem.item_stitch_disabled = item.item_stitch_disabled || 0; // 是否开启跟拍
  }

  newItem.video_info = item.video_info;
  // 电商锚点需求，将anchors字段放入video_info中，复制adGroup场景下的数据回填的数据格式和新建保持一致
  if (!_.isEmpty(item.anchors)) {
    _.set(newItem, 'video_info.anchors', item.anchors);
  }
  // 帖子头像
  if (item.video_info) {
    newItem.video_info.avatar_icon = item.avatar_icon || {};
  }

  // identity结构白名单的创意 有identity相关属性
  // 全量之后 这部分可在上面newItem对象中直接赋值
  if (isCreativeHasIdentityWhite()) {
    newItem.identity_type = item.identity_type || 0;
    newItem.identity_id = item.identity_id || '';
    newItem.identity_data = {
      avatar_icon: item.identity_id ? item.avatar_icon : {},
      display_name: item.identity_id ? (item.source || item.app_name) : '',
      identity_type: item.identity_type || 0,
      identity_id: item.identity_id || '',
    };
    if (bcIdentityTypesList.includes(item.identity_type)) {
      newItem.identity_bc_id = item.identity_bc_id || '';
      newItem.identity_data.identity_bc_id = item.identity_bc_id || '';
    }
  }

  /**
   * DPA创意: image_info固定是[]，没有video_info字段
   * 单图创意：image_info包含具体图片信息，没有video_info字段
   * 单视频创意：video_info和image_info都不为空
  */
  if (Array.isArray(newItem.image_info) && newItem.image_info.length === 0) {
    newItem.video_info = item.video_info || {};
    // 判断原生广告是否有视频信息
    const hasVideoInfo = Object.keys(newItem.video_info).length;
    if (item.item_source === NATIVE_VIDEO.value && !hasVideoInfo) {
      delete newItem.image_info;
      delete newItem.video_info;
    }
  }
  // Collection Ads 清掉 page id 都走新建
  // if (newItem.creative_type === CATALOG_CREATIVE_TYPE.LYNX_PLP) {
  //   newItem.page_id = '';
  // }

  /** 原生推视频不允许复制, 需清空素材重新选择 */
  const isTTBAPushVideoNativeAd =
    newItem.identity_type === identityTypeMap.TT_USER &&
    newItem.nativeAdActionType === NATIVE_AD_ACTION_TYPE.PUSH_VIDEO_TO_TTBA;

  if (isTTBAPushVideoNativeAd) {
    delete newItem.video_info;
    delete newItem.image_info;
    newItem.image_mode = imageMode.VIDEO_TYPE_3;
    newItem.dark_post_status = DARK_POST_STATUS_MAP.OFF;
    newItem.bc_restrict_delivery = false;
  }

  return newItem;
};
